import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, ChevronRight, Sparkles, FolderPlus, Zap } from "lucide-react";
import { toast } from "sonner";

import { ChannelIcon, NotionIcon, LinkedInIcon } from "@/components/BrandIcons";
import { FileSpreadsheet } from "lucide-react";

const CHANNELS = [
  { key: "gmail", label: "Gmail" },
  { key: "slack", label: "Slack" },
  { key: "teams", label: "Microsoft Teams" },
  { key: "calendar", label: "Google Calendar" },
  { key: "zoom", label: "Zoom" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "notion", label: "Notion" },
  { key: "linkedin", label: "LinkedIn" },
];

const STEP_LABELS = ["Welcome", "Channels", "Founder", "Import", "Setup"];

const PROCESS_TASKS = [
  "Creating your workspace",
  "Wiring connected channels",
  "Saving founder context",
  "Importing projects & people",
  "Generating your first Morning Brief",
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [step, setStep] = useState(0);
  const [connected, setConnected] = useState(new Set(["gmail", "slack", "calendar"]));
  const [founders, setFounders] = useState([{ name: "", style: "", priorities: "" }]);
  const [ctx, setCtx] = useState({
    timezone: "America/New_York",
    update_frequency: "daily",
  });
  const [importChoice, setImportChoice] = useState("demo");
  const [doneTasks, setDoneTasks] = useState(0);

  const skip = async () => {
    await endpoints.auth.completeOnboarding();
    await checkAuth();
    navigate("/", { replace: true });
  };

  const finalize = async () => {
    setStep(4);
    const timer = setInterval(() => setDoneTasks((d) => Math.min(d + 1, PROCESS_TASKS.length - 1)), 700);
    try {
      if (importChoice === "demo") await endpoints.demoReset();
      const settings = await endpoints.settings();
      const bag = Object.fromEntries(settings.map((s) => [s.key, s]));
      const setKey = async (key, value) => {
        if (bag[key]) await endpoints.updateSetting(bag[key].id, { value });
        else await endpoints.createSetting({ key, value });
      };
      for (const c of CHANNELS) {
        await setKey(`channel_${c.key}`, connected.has(c.key) ? "connected" : "disconnected");
      }
      // Founder 1 uses the legacy keys; Founder 2 (if present) uses founder2_*.
      const founderKeyMap = [
        { name: "founder_name", style: "founder_style", priorities: "founder_priorities" },
        { name: "founder2_name", style: "founder2_style", priorities: "founder2_priorities" },
      ];
      for (let i = 0; i < founders.length; i++) {
        const f = founders[i];
        const km = founderKeyMap[i];
        if (!km) break;
        if (f.name && f.name.trim()) await setKey(km.name, f.name.trim());
        if (f.style && f.style.trim()) await setKey(km.style, f.style.trim());
        if (f.priorities && f.priorities.trim()) await setKey(km.priorities, f.priorities.trim());
      }
      for (const [k, v] of Object.entries(ctx)) {
        if (v && v.trim()) await setKey(k, v.trim());
      }
      await endpoints.auth.completeOnboarding();
      clearInterval(timer);
      setDoneTasks(PROCESS_TASKS.length);
      await checkAuth();
      setTimeout(() => navigate("/", { replace: true }), 800);
    } catch (e) {
      clearInterval(timer);
      toast.error("Setup failed — please try again");
      setStep(3);
    }
  };

  useEffect(() => {
    if (user && user.onboarding_complete) navigate("/", { replace: true });
  }, [user, navigate]);

  const toggleChannel = (key) => {
    const next = new Set(connected);
    next.has(key) ? next.delete(key) : next.add(key);
    setConnected(next);
  };

  const inputStyle = { borderColor: "#E5E5E5", color: "#0A0A0A", background: "#FFFFFF" };

  return (
    <div className="min-h-screen flex flex-col items-center p-4 pt-10 md:pt-16" style={{ background: "#FAFAFA", color: "#0A0A0A" }} data-testid="onboarding-page">
      {/* Stepper */}
      <div className="flex items-center gap-2 mb-10">
        {STEP_LABELS.map((label, i) => (
          <React.Fragment key={label}>
            <div className="flex items-center gap-1.5">
              <div
                className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] mono font-semibold"
                style={{
                  background: i < step ? "#737373" : i === step ? "#FAFAFA" : "#E5E5E5",
                  color: i <= step ? "#fff" : "#737373",
                }}
              >
                {i < step ? <Check size={12} /> : i + 1}
              </div>
              <span className="hidden sm:block text-[10px] mono" style={{ color: i === step ? "#FAFAFA" : "#A3A3A3" }}>
                {label.toUpperCase()}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && <div className="w-6 h-px" style={{ background: "#E5E5E5" }} />}
          </React.Fragment>
        ))}
      </div>

      <div className="w-full max-w-xl animate-fade-in" key={step}>
        {step === 0 && (
          <div className="text-center">
            <div className="h-14 w-14 mx-auto rounded-xl flex items-center justify-center mb-6" style={{ background: "#0A0A0A", color: "#FFFFFF" }}>
              <span className="text-2xl font-bold">F</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-3">
              Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
            </h1>
            <p className="text-sm leading-relaxed max-w-md mx-auto mb-8" style={{ color: "#737373" }}>
              Priority Zero is your command center. Every project, person, commitment, and escalation
              in one place, with an AI copilot built for the operator in the room.
            </p>
            <button onClick={() => setStep(1)} className="text-sm px-6 py-2.5 rounded-md font-medium inline-flex items-center gap-2" style={{ background: "#0A0A0A", color: "#FFFFFF" }} data-testid="onboarding-start-btn">
              Get started <ChevronRight size={14} />
            </button>
            <div className="mt-4">
              <button onClick={skip} className="text-xs hover:text-neutral-900" style={{ color: "#A3A3A3" }} data-testid="onboarding-skip-btn">
                Skip setup for now
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <h2 className="text-xl font-bold tracking-tight mb-1">Where do updates come from?</h2>
            <p className="text-xs mb-5" style={{ color: "#737373" }}>
              Mark the channels your work already lives in — we&apos;ll wire the real integrations later. For Gmail and Slack, you can connect with your own OAuth credentials from <span className="text-neutral-900 font-medium">Settings → Connections</span> after setup.
            </p>
            <div className="grid grid-cols-2 gap-2.5 mb-6">
              {CHANNELS.map((c) => {
                const on = connected.has(c.key);
                return (
                  <button
                    key={c.key}
                    onClick={() => toggleChannel(c.key)}
                    data-testid={`onboarding-channel-${c.key}`}
                    className="fos-card p-3 flex items-center gap-2.5 text-left transition-colors"
                    style={{ borderColor: on ? "#FAFAFA" : "#E5E5E5" }}
                  >
                    <span className="text-neutral-400">
                      <ChannelIcon channel={c.key} size={16} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{c.label}</p>
                      <p className="text-[10px] mono" style={{ color: on ? "#737373" : "#A3A3A3" }}>
                        {on ? "● IN USE" : "○ NOT USED"}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
            <StepNav onBack={() => setStep(0)} onNext={() => setStep(2)} />
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="text-xl font-bold tracking-tight mb-1">Founder context</h2>
            <p className="text-xs mb-5" style={{ color: "#737373" }}>
              Teach the AI how your founder operates. This shapes every brief, draft, and escalation.
            </p>
            <div className="space-y-4 mb-6">
              {founders.map((f, i) => (
                <div
                  key={i}
                  className="fos-card p-4 space-y-3"
                  data-testid={`onboarding-founder-card-${i}`}
                >
                  {founders.length > 1 && (
                    <div className="flex items-center justify-between -mb-1">
                      <span className="text-[10px] mono" style={{ color: "#737373" }}>
                        FOUNDER {i + 1}
                      </span>
                      {i > 0 && (
                        <button
                          type="button"
                          onClick={() => setFounders(founders.filter((_, idx) => idx !== i))}
                          className="text-neutral-400 hover:text-red-600 p-0.5 rounded"
                          data-testid={`onboarding-founder-remove-${i}`}
                          title="Remove this founder"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  )}
                  <Field
                    label="FOUNDER NAME"
                    value={f.name}
                    onChange={(v) => setFounders(founders.map((x, idx) => (idx === i ? { ...x, name: v } : x)))}
                    placeholder="e.g. Jonathan Reeves"
                    testid={i === 0 ? "onboarding-founder-name" : `onboarding-founder-name-${i}`}
                    style={inputStyle}
                  />
                  <Field
                    label="COMMUNICATION STYLE"
                    value={f.style}
                    onChange={(v) => setFounders(founders.map((x, idx) => (idx === i ? { ...x, style: v } : x)))}
                    placeholder="e.g. Short Slack DMs, decisions only"
                    testid={i === 0 ? "onboarding-founder-style" : `onboarding-founder-style-${i}`}
                    style={inputStyle}
                  />
                  <Field
                    label="TOP PRIORITIES"
                    value={f.priorities}
                    onChange={(v) => setFounders(founders.map((x, idx) => (idx === i ? { ...x, priorities: v } : x)))}
                    placeholder="e.g. Revenue, retention, hiring velocity"
                    testid={i === 0 ? "onboarding-founder-priorities" : `onboarding-founder-priorities-${i}`}
                    style={inputStyle}
                  />
                </div>
              ))}
              {founders.length < 2 && (
                <button
                  type="button"
                  onClick={() => setFounders([...founders, { name: "", style: "", priorities: "" }])}
                  className="text-xs font-semibold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
                  data-testid="onboarding-add-founder-btn"
                >
                  + Add another founder
                </button>
              )}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <Field label="TIMEZONE" value={ctx.timezone} onChange={(v) => setCtx({ ...ctx, timezone: v })} testid="onboarding-tz" style={inputStyle} />
                <div>
                  <p className="text-[10px] mono mb-1" style={{ color: "#737373" }}>UPDATE FREQUENCY</p>
                  <select
                    value={ctx.update_frequency}
                    onChange={(e) => setCtx({ ...ctx, update_frequency: e.target.value })}
                    className="w-full text-sm px-3 py-2 rounded-md border outline-none"
                    style={inputStyle}
                    data-testid="onboarding-frequency"
                  >
                    <option value="realtime">Real-time</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
              </div>
            </div>
            <StepNav onBack={() => setStep(1)} onNext={() => setStep(3)} />
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 className="text-xl font-bold tracking-tight mb-1">Import your projects</h2>
            <p className="text-xs mb-5" style={{ color: "#737373" }}>
              Start with a fully-seeded demo workspace, or begin from a clean slate.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              <ImportCard
                active={importChoice === "demo"}
                onClick={() => setImportChoice("demo")}
                icon={<Zap size={18} style={{ color: "#FF9F0A" }} />}
                title="Load demo workspace"
                desc="5 projects, 8 people, 12 commitments, 4 SOPs, live escalations — see the OS at full power."
                testid="onboarding-import-demo"
              />
              <ImportCard
                active={importChoice === "fresh"}
                onClick={() => setImportChoice("fresh")}
                icon={<FolderPlus size={18} style={{ color: "#D4D4D4" }} />}
                title="Start fresh"
                desc="An empty workspace. Add folders, projects and people as you go."
                testid="onboarding-import-fresh"
              />
            </div>
            <StepNav onBack={() => setStep(2)} onNext={finalize} nextLabel="Finish setup" nextTestid="onboarding-finish-btn" />
          </div>
        )}

        {step === 4 && (
          <div className="text-center" data-testid="onboarding-processing">
            <Sparkles size={28} className="mx-auto mb-5" style={{ color: "#D4D4D4" }} />
            <h2 className="text-xl font-bold tracking-tight mb-6">Setting up your command center…</h2>
            <div className="max-w-sm mx-auto space-y-2.5 text-left">
              {PROCESS_TASKS.map((t, i) => (
                <div key={t} className="flex items-center gap-2.5 text-sm" style={{ opacity: i <= doneTasks ? 1 : 0.35 }}>
                  {i < doneTasks ? (
                    <span className="h-5 w-5 rounded-full flex items-center justify-center" style={{ background: "#737373" }}>
                      <Check size={12} />
                    </span>
                  ) : i === doneTasks ? (
                    <span className="h-5 w-5 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: "#FAFAFA", borderTopColor: "transparent" }} />
                  ) : (
                    <span className="h-5 w-5 rounded-full border" style={{ borderColor: "#E5E5E5" }} />
                  )}
                  <span style={{ color: i < doneTasks ? "#FAFAFA" : "#737373" }}>{t}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StepNav({ onBack, onNext, nextLabel = "Continue", nextTestid = "onboarding-next-btn" }) {
  return (
    <div className="flex items-center justify-between">
      <button onClick={onBack} className="text-xs hover:text-neutral-900" style={{ color: "#737373" }} data-testid="onboarding-back-btn">
        ← Back
      </button>
      <button onClick={onNext} className="text-sm px-5 py-2 rounded-md font-medium inline-flex items-center gap-2" style={{ background: "#0A0A0A", color: "#FFFFFF" }} data-testid={nextTestid}>
        {nextLabel} <ChevronRight size={14} />
      </button>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid, style }) {
  return (
    <div>
      <p className="text-[10px] mono mb-1" style={{ color: "#737373" }}>{label}</p>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full text-sm px-3 py-2 rounded-md border outline-none"
        style={style}
      />
    </div>
  );
}

function ImportCard({ active, onClick, icon, title, desc, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="fos-card p-4 text-left transition-colors"
      style={{ borderColor: active ? "#FAFAFA" : "#E5E5E5" }}
    >
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-sm font-semibold">{title}</span>
        {active && <Check size={14} className="ml-auto" style={{ color: "#0A0A0A" }} />}
      </div>
      <p className="text-xs leading-relaxed" style={{ color: "#737373" }}>{desc}</p>
    </button>
  );
}
